import type { ComponentType, FunctionComponent } from 'react'
import { useMemo } from 'react'

import useTourV2VerticalName from 'hooks/TourV2/useTourV2VerticalName'
import GlobalHotelsSearchTabContent from 'components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalHotelsSearchTabContent'
import lazyImport from 'components/utils/lazyImport'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineHomeAltIcon from 'LuxKit/Icons/line/LineHomeAltIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineShopIcon from 'LuxKit/Icons/line/LineShopIcon'
import { arrayToMap } from 'lib/array/arrayUtils'
import useGlobalSearchVerticals from './useGlobalSearchVerticals'

// Hotels stay eager for the homepage/desktop search critical path. Other
// verticals are only needed when their tab is selected.
const GlobalHomesAndVillasSearchTabContent = lazyImport(() => import('components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalHomesAndVillasSearchTabContent'))
const GlobalTourSearchTabContent = lazyImport(() => import('components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalTourSearchTabContent'))
const GlobalExperiencesSearchTabContent = lazyImport(() => import('components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalExperiencesSearchTabContent'))
const GlobalFlightsSearchTabContent = lazyImport(() => import('components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalFlightsSearchTabContent'))
const GlobalCruisesSearchTabContent = lazyImport(() => import('components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalCruisesSearchTabContent'))
const GlobalCarHireSearchTabContent = lazyImport(() => import('components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalCarHireSearchTabContent'))
const CarsGlobalSearchControls = lazyImport(() => import('components/Cars/CarsGlobalSearchControls'))
const GlobalMerchandiseSearchTabContent = lazyImport(() => import('components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalMerchandiseSearchTabContent'))

const SEARCH_VERTICALS_TAB_COMPONENTS: Record<App.SearchVertical, ComponentType> = {
  'car-hire': GlobalCarHireSearchTabContent,
  cars: CarsGlobalSearchControls,
  cruises: GlobalCruisesSearchTabContent,
  experiences: GlobalExperiencesSearchTabContent,
  flights: GlobalFlightsSearchTabContent,
  'homes-and-villas': GlobalHomesAndVillasSearchTabContent,
  hotels: GlobalHotelsSearchTabContent,
  tours: GlobalTourSearchTabContent,
  merchandise: GlobalMerchandiseSearchTabContent,
}

const SEARCH_VERTICALS_TAB_ICONS: Record<App.SearchVertical, FunctionComponent> = {
  'car-hire': LineCarIcon,
  cars: LineCarIcon,
  cruises: LineShipIcon,
  experiences: LineHotAirBalloonIcon,
  flights: LinePlaneDepartureIcon,
  'homes-and-villas': LineHomeAltIcon,
  hotels: LineBedIcon,
  tours: LineCompassIcon,
  merchandise: LineShopIcon,
}

function useGlobalSearchVerticalTabs(): Map<App.SearchVertical, App.GlobalSearchVerticalTab> {
  const verticals = useGlobalSearchVerticals()
  const toursVerticalName = useTourV2VerticalName().toLowerCase()

  const globalSearchVerticals = useMemo(() => {
    return arrayToMap(
      verticals,
      vertical => vertical.vertical,
      (vertical): App.GlobalSearchVerticalTab => {
        return {
          id: vertical.vertical,
          label: vertical.vertical === 'tours' ? toursVerticalName : vertical.label,
          Component: SEARCH_VERTICALS_TAB_COMPONENTS[vertical.vertical],
          Icon: SEARCH_VERTICALS_TAB_ICONS[vertical.vertical],
        }
      })
  }, [verticals, toursVerticalName])

  return globalSearchVerticals
}

export default useGlobalSearchVerticalTabs
